import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import { invalidateScoped, patchScoped } from "@/queries/invalidation";
import {
  mcpAccessKey,
  mcpAccessQuery,
  mcpClientsKey,
  mcpClientsQuery,
  myOAuthConsentsKey,
  myOAuthConsentsQuery,
  oauthAppKey,
  oauthAppQuery,
  oauthAppsKey,
  oauthAppsQuery,
  oauthAppTenant,
  type OAuthAppOwner,
} from "@/queries/oauth-apps";
import { GLOBAL_TENANT } from "@/queries/scope";
import {
  OAuthIdpService,
  type McpClient,
  type OAuthApp,
  type OAuthConsent,
} from "@/services/oauth-idp.service";

/*
 * The OAuth app and MCP access readers (`app/queries/oauth-apps.ts`, NG-2814),
 * with the cache edits each screen makes after a write. These screens have no
 * store; they write through the service and then patch or re-read the cached
 * entries here, so every reader on screen shows a confirmed write. All must be
 * called from an `observer` component.
 */

const service = new OAuthIdpService();

const NO_CLIENTS: McpClient[] = [];

/** The HTTP status of a failed read, if it had one. */
function statusOf(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

/** An owner's OAuth apps, with `refresh` for after a create or delete. */
export function useOAuthApps(owner: OAuthAppOwner) {
  const client = useQueryClient();
  const tenant = oauthAppTenant(owner);
  const { mode, query } = useScopedQuery(tenant, (scope) => oauthAppsQuery(service, scope, owner));
  const scope = useQueryScope(mode === "cache" ? tenant : undefined);
  return {
    apps: query.data,
    isLoading: mode === "waiting" || query.isPending,
    error: query.error,
    /** The read was refused: the viewer may not list these apps. */
    forbidden: statusOf(query.error) === 403,
    /** Re-read the list after a create or delete. */
    refresh: async () => {
      if (scope) await invalidateScoped(client, scope, "oauth-apps", "list");
    },
  };
}

/** One OAuth app, with the cache edits its page makes. */
export function useOAuthApp(owner: OAuthAppOwner, appId: string) {
  const client = useQueryClient();
  const tenant = oauthAppTenant(owner);
  const { mode, query } = useScopedQuery(
    tenant,
    (scope) => oauthAppQuery(service, scope, owner, appId),
    { enabled: Boolean(appId) },
  );
  const scope = useQueryScope(mode === "cache" ? tenant : undefined);
  return {
    app: query.data,
    isLoading: mode === "waiting" || query.isPending,
    error: query.error,
    /** The read was refused: the viewer may not see this app. */
    forbidden: statusOf(query.error) === 403,
    /** Store a confirmed save: the app's entry, and its row in the cached list. */
    storeApp: (updated: OAuthApp) => {
      if (!scope) return;
      client.setQueryData(oauthAppKey(scope, appId), updated);
      patchScoped<OAuthApp[]>(client, oauthAppsKey(scope), (apps) =>
        apps.map((item) => (item.id === updated.id ? updated : item)),
      );
    },
    /** Apply a confirmed sub-resource write (a redirect URI) to the app's entry. */
    patchApp: (update: (app: OAuthApp) => OAuthApp) => {
      if (scope) patchScoped<OAuthApp>(client, oauthAppKey(scope, appId), update);
    },
    /** Drop a deleted app from the cache, and re-read the list. */
    forgetApp: async () => {
      if (!scope) return;
      client.removeQueries({ queryKey: oauthAppKey(scope, appId) });
      await invalidateScoped(client, scope, "oauth-apps", "list");
    },
  };
}

/** The signed-in user's own OAuth grants, with the edit a revoke makes. */
export function useMyOAuthConsents() {
  const client = useQueryClient();
  const { mode, query } = useScopedQuery(GLOBAL_TENANT, (scope) =>
    myOAuthConsentsQuery(service, scope),
  );
  const scope = useQueryScope(mode === "cache" ? GLOBAL_TENANT : undefined);
  return {
    consents: query.data,
    error: query.error,
    /** Drop a revoked grant from the list. */
    dropConsent: (consentId: string) => {
      if (!scope) return;
      patchScoped<OAuthConsent[]>(client, myOAuthConsentsKey(scope), (consents) =>
        consents.filter((consent) => consent.id !== consentId),
      );
    },
  };
}

/** A workspace's MCP access and its connected clients, with the edits the card makes. */
export function useMcpAccess(workspaceSlug: string) {
  const client = useQueryClient();
  const { mode, query: access } = useScopedQuery(
    workspaceSlug,
    (scope) => mcpAccessQuery(service, scope),
    { enabled: Boolean(workspaceSlug) },
  );
  const { query: clients } = useScopedQuery(
    workspaceSlug,
    (scope) => mcpClientsQuery(service, scope),
    { enabled: Boolean(workspaceSlug) },
  );
  const scope = useQueryScope(mode === "cache" ? workspaceSlug : undefined);
  return {
    access: access.data,
    error: access.error,
    clients: clients.data ?? NO_CLIENTS,
    /** The viewer may see who is connected: the list answered rather than refusing. */
    canListClients: clients.isSuccess,
    /** Store a confirmed save of the access settings. */
    storeAccess: (updated: NonNullable<typeof access.data>) => {
      if (scope) client.setQueryData(mcpAccessKey(scope), updated);
    },
    /** Re-read the access and the clients after Disable. */
    refresh: async () => {
      if (scope) await invalidateScoped(client, scope, "mcp");
    },
    /** Drop a revoked client from the list. */
    dropClient: (consentId: string) => {
      if (!scope) return;
      patchScoped<McpClient[]>(client, mcpClientsKey(scope), (list) =>
        list.filter((item) => item.consent_id !== consentId),
      );
    },
  };
}

/** The service the OAuth screens write through. */
export const oauthIdpService = service;
